import React, { useEffect, useState } from "react";
import { CartMenuClose, CartMenuOpen } from "./cartMenuButtons";
import { navigate } from "gatsby";
import PrimaryButton from "../shared/primaryButton";
import { $cartStore, $isSideCartOpen, loadCart } from "../../utils/store";
import { useStore } from "@nanostores/react";
import { CartData, CartProduct } from "../../utils/cartTypes";
import { formatPrice, updateProductQuantity, printPrice, gtag } from "../../utils/utils";
import EmptyCart from "./emptyCart";
import ApplyCoupon from "./applyCoupon";
import ProductLoadingOverlay from "../shared/productLoadingOverlay";
import { useOptions } from "../../hooks/useOptions";
import { useProduct } from "../../hooks/useProduct";
import { __t } from "../../utils/translate";
import { useCartData } from "../../hooks/useCartData";
import { getShortLinkCart } from "@/utils/api";

const CartSidebar: React.FC<React.PropsWithChildren> = ({ className, options }) => {
    const cart = useStore($cartStore);
    const isMenuOpen = useStore($isSideCartOpen);
    const [isLoading, setIsLoading] = useState(true);
    const [count, setCount] = useState<null | number>(null);
    const [total, setTotal] = useState<null | string>(null);

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());

        if (params.ck) {
            getShortLinkCart(params.ck).then(() => {
                loadCart();
            });
        } else {
            loadCart();
        }
    }, []);

    useEffect(() => {
        if (cart !== null) {
            calculateCart();
        }
    }, [cart]);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    const calculateCart = () => {
        setIsLoading(false);
        let sum = 0;

        if (cart?.cart && Object.keys(cart.cart).length > 0) {
            Object.values(cart.cart).forEach(product => {
                if (!product.hasOwnProperty("bundled_by")) {
                    sum += product.quantity;
                }
            });
            let decimals = typeof options.decimals === 'number' ? options.decimals : 2;
            setTotal(String((cart.stripe_cart.order_data.total.amount / 100).toFixed(decimals)));
        } else {
            setTotal(null);
        }

        sum > 0 ? setCount(sum) : setCount(null);
    };

    return (
        <div className="flex items-center justify-center">
            {/* prettier-ignore */}
            <Cart
                className={`flex max-h-screen md:h-dynamic w-full max-w-[300px] lg:max-w-[370px]
                    flex-col items-start justify-start overflow-hidden`}
                showCart={isMenuOpen}
                isMenuOpen={isMenuOpen}
                cart={cart}
                isLoading={isLoading}
            />
            <div onClick={() => $isSideCartOpen.set(true)}
                className={`${className} lg:border lg:rounded border-dark-blu/20 lg:px-5 h-[44px] lg:h-[56px] flex items-center cursor-pointer lg:ml-5 lg:mr-2
                ${isMenuOpen ? "opacity-0 pointer-events-none invisible" : ""} 
`}
            >
                <div className="flex flex-row flex-nowrap gap-2 relative">
                    <div className="hidden lg:block text-green text-title font-bold">
                        <span className="inline whitespace-nowrap" dangerouslySetInnerHTML={{ __html: options.currency_symbol }} />
                        {total ? total : formatPrice(0.00)}
                    </div>
                    <CartMenuOpen className="h-[26px]" />

                    <div className="absolute right-2 top-[10px] h-4 w-4 text-[13px] text-button-accent-color flex items-center justify-center">
                        {count ? count : 0}
                    </div>

                </div>
            </div>
        </div>
    );
};

type CartProps = {
    className?: string;
    showCart?: boolean;
    isMenuOpen?: boolean;
    isLoading: boolean;
    cart: CartData | null;
};

const Cart: React.FC<CartProps> = ({
    className = "",
    showCart = true,
    isMenuOpen = false,
    isLoading,
    cart,
}) => {
    const onClick = () => $isSideCartOpen.set(false);

    const isCartEmpty = typeof cart?.cart !== 'object' || cart.cart?.length === 0;

    return (
        <>
            <div
                // lg:bg-transparent
                // prettier-ignore
                className={`${isMenuOpen ? "bg-black bg-opacity-70 opacity-100 z-40" : "opacity-0 z-[-10]"} fixed left-0 bottom-0 transition-all ease-in-out duration-500 w-full h-full overflow-hidden`}
                onClick={onClick}
            ></div>
            <div id='cart-sidebar' className={`${isMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-96 "} 
                    fixed right-0 bottom-0 transition-all ease-in-out duration-300 z-[70]`}>
                <div className="flex justify-end w-full m-auto overflow-y-auto min-w-[300px] lg:min-w-[370px]" >
                    <div
                        className={`${showCart ? "" : "hidden"}
                ${className} bg-white px-5 py-5 sm:py-6 md:px-6 lg:px-5 z-[60] relative`}
                    >
                        <div className="absolute top-3 right-1 md:right-3">
                            <CartMenuClose />
                        </div>
                        {isCartEmpty ? (
                            <EmptyCart className="" />
                        ) : (
                            <NonEmptyCart cart={cart} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

type NonEmptyCart = {
    cart: CartData;
};

const NonEmptyCart: React.FC<NonEmptyCart> = ({ cart }) => {
    const { products, subtotal, discount, total, coupon_amount, applied_coupons } = useCartData();
    const options = useOptions();

    const currency = options.wpOptions.data.currency;

    const handleCheckout = () => {
        $isSideCartOpen.set(false);

        // prettier-ignore
        let data = {
            currency: currency,
            value: String((total / 100).toFixed(2)),
            coupon: applied_coupons?.join(', '),
            items: products.map(product => {
                let item = {
                    item_id: product.product_id,
                    item_name: product.name,
                    category: product.category,
                    price: product.price,
                    quantity: product.quantity,
                    sku: product.sku,
                    slug: product.slug,
                    currency,
                }
                if (product.attribute_summary) {
                    item.variant = product.attribute_summary;
                }
                if (product.parent_name) {
                    item.item_name = product.parent_name;
                }

                return item;
            })
        };
        gtag("event", "begin_checkout", data);
        navigate("/checkout");
    };

    const handleViewCart = () => {
        $isSideCartOpen.set(false);
        navigate("/cart");
    };

    const currency_symbol = options.wpOptions.data.currency_symbol

    return (
        <div className="flex flex-col justify-between h-full w-full flex-auto pt-3 lg:pt-2 text-dark">
            <div className="mb-[32px] overflow-y-auto cart-sidebar-inner md:mb-3 md:h-full md:max-h-[70%] lg:max-h-[65%] xl:max-h-[62%]">
                {products.map((p) => {
                    return <ProductCardSm key={p.key} product={p} />;
                })}
            </div>
            <div className="h-fit">
                {/* <ApplyCoupon isForm={false} /> */}
                <div className="hidden md:flex items-end justify-between pb-5 border-b border-stroke-color mb-4">
                    <div className="text-base-16">{__t("Subtotal")}:</div>
                    <div className="text-18 lg:text-h2-r font-semibold"><div className="inline" dangerouslySetInnerHTML={{ __html: currency_symbol }} />{formatPrice(subtotal)}</div>
                </div>
                {discount ? (
                    <div className="mb-[20px] hidden md:flex items-end justify-between">
                        <div className="">{__t("Coupon")} {coupon_amount ? <span className="text-base-16-strong">({coupon_amount}%)</span> : ''}</div>
                        <div className=""><div className="inline" dangerouslySetInnerHTML={{ __html: currency_symbol }} />{formatPrice(discount)}</div>
                    </div>
                ) : (<></>)}

                {/* <div className="mb-[20px] pb-[20px] border-b border-stroke-color flex items-end justify-between">
                    <div className="text-h3-r text-body-text-color">{__t("Shipping")}</div>
                    <div className="text-button-accent-color text-h3-r">{__t("Free")}</div>
                </div>
                <div className="mb-6 pb-[10px] flex items-end justify-between">
                    <div className="text-title text-body-text-color uppercase">{__t("Total")}</div>
                    <div className="text-main-text-color text-title"><div className="inline" dangerouslySetInnerHTML={{ __html: currency_symbol }} />{formatPrice(total)}</div>
                </div> */}
                <PrimaryButton
                    className="mb-[10px] h-[50px] w-full rounded-sm"
                    textStyle="!text-base-16-strong !font-bold"
                    type="submit"
                    onClick={handleCheckout}
                    testid="to_checkout"
                >
                    {__t("Checkout")}
                </PrimaryButton>
                <PrimaryButton
                    className="group h-[50px] w-full rounded-sm border border-stroke-color !bg-white hover:!bg-light-gray hover:underline mb-1 lg:mb-5"
                    textStyle="!text-[#333] !text-base-16"
                    onClick={handleViewCart}
                >
                    {__t("View cart")}
                </PrimaryButton>
            </div>

        </div>
    );
};

type ProductCardSmProps = {
    product: CartProduct;
};

const ProductCardSm: React.FC<ProductCardSmProps> = ({ product }) => {
    const [isUpdating, setIsUpdating] = useState(false);

    const { handleMinus, handlePlus } =
        useProduct(product);

    const handleRemove = async () => {
        setIsUpdating(true);
        await updateProductQuantity(product.key, 0);
        setIsUpdating(false);
    };

    const options = useOptions();
    const currency_symbol = options.wpOptions.data.currency_symbol

    const renderAttributes = () => {
        if (!product.variation) return null;

        return Object.entries(product.variation).map(([key, value]) => (
            <p key={key} className="text-small-14 font-semibold leading-loose text-dark  mb-1">
                {`${value.label}: `}<span className="text-small-14 font-normal">{value.value}</span>
            </p>
        ));
    };

    const renderBundleItemAttributes = (variation) => {
        if (!variation || variation.length === 0) return null;


        return Object.entries(variation).map(([key, value]) => (
            <p key={key} className="text-small-14 font-medium leading-loose text-main-text-color mb-1">
                {`${value.label}: `}<span className="text-small-14 font-normal">{value.value}</span>
            </p>
        ));
    };

    return (
        product.bundled_items && product.bundled_items.length > 0 ? (
            <div className="flex flex-col mt-3">
                <div className="relative flex items-start border-stroke-color pt-4 border-t min-h-[80px]">
                    <button
                        onClick={handleRemove}
                        className="py-2 pr-3"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 32 32" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M24.9428 8.94265C25.4635 8.42195 25.4635 7.57773 24.9428 7.05703C24.4221 6.53633 23.5779 6.53633 23.0572 7.05703L16 14.1142L8.94283 7.05703C8.42213 6.53633 7.57791 6.53633 7.05721 7.05703C6.53651 7.57773 6.53651 8.42195 7.05721 8.94265L14.1144 15.9998L7.05721 23.057C6.53651 23.5777 6.53651 24.4219 7.05721 24.9426C7.57791 25.4633 8.42213 25.4633 8.94283 24.9426L16 17.8855L23.0572 24.9426C23.5779 25.4633 24.4221 25.4633 24.9428 24.9426C25.4635 24.4219 25.4635 23.5777 24.9428 23.057L17.8856 15.9998L24.9428 8.94265Z" fill="#B6C5D1" />
                        </svg>
                    </button>
                    <ProductLoadingOverlay isUpdating={isUpdating} />
                    <div className="flex w-full gap-0 md:gap-3">
                        <p className="max-w-[230px] mb-3 whitespace-normal text-small-14 md:text-base-16 md:!text-[15px] text-dark truncate">
                            {product.name}
                        </p>
                    </div>
                    <img
                        className="h-[65px] w-[65px] border border-stroke-color"
                        src={product.image.src}
                        alt={product.image.altText}
                    />
                </div>
                <span className="inline-block text-small-14 font-semibold text-dark ml-6">
                    {__t("Includes")}:
                </span>
                {product.bundled_items?.length > 0 && product.bundled_items.map(item => (
                    <div className="flex flex-col sm:flex-row items-start gap-1 ml-6" key={item.name}>
                        <div className="flex flex-col items-start w-full">
                            <div key={item.id} className="flex flex-row justify-between w-full">
                                <span className="whitespace-normal text-small-14 leading-loose text-dark truncate">{item.name}<span className="whitespace-nowrap"> × {item.quantity}</span></span>
                            </div>
                            {/* {renderBundleItemAttributes(item.variation)} */}
                        </div>
                    </div>
                ))}
                <div className="ml-6 whitespace-nowrap text-18 lg:text-h2-r text-gray font-semibold">
                    <span className="text-[12px] font-normal pr-1 inline-block">{product.quantity} × </span>
                    <div className="inline" dangerouslySetInnerHTML={{ __html: currency_symbol }} />{printPrice(product.price) || ""}
                </div>
            </div>
        ) : (
            <div className="relative flex items-start border-t border-stroke-color pt-4 pb-1 mt-3 min-h-[116px] md:min-h-[96px]">
                <button
                    onClick={handleRemove}
                    className="py-2 pr-3"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 32 32" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M24.9428 8.94265C25.4635 8.42195 25.4635 7.57773 24.9428 7.05703C24.4221 6.53633 23.5779 6.53633 23.0572 7.05703L16 14.1142L8.94283 7.05703C8.42213 6.53633 7.57791 6.53633 7.05721 7.05703C6.53651 7.57773 6.53651 8.42195 7.05721 8.94265L14.1144 15.9998L7.05721 23.057C6.53651 23.5777 6.53651 24.4219 7.05721 24.9426C7.57791 25.4633 8.42213 25.4633 8.94283 24.9426L16 17.8855L23.0572 24.9426C23.5779 25.4633 24.4221 25.4633 24.9428 24.9426C25.4635 24.4219 25.4635 23.5777 24.9428 23.057L17.8856 15.9998L24.9428 8.94265Z" fill="#B6C5D1" />
                    </svg>
                </button>
                <ProductLoadingOverlay isUpdating={isUpdating} />
                <div className="flex w-full gap-2 md:gap-3">
                    <div className="flex flex-col gap-0">
                        <p className="mb-3 whitespace-normal text-small-14 md:text-base-16 md:!text-[15px] text-dark truncate">
                            {product.name}
                            {Object.entries(product.variation).length === 1 && Object.entries(product.variation).map(([key, value]) => (
                                <> - {value.value}</>
                            ))}
                        </p>
                        {Object.entries(product.variation).length > 1 &&
                            <div className="mb-[4px] text-base-16">
                                <div className="flex flex-col">
                                    {renderAttributes()}
                                </div>
                            </div>
                        }
                        <div className="whitespace-nowrap text-18 lg:text-h2-r text-gray font-semibold">
                            <span className="text-[12px] font-normal pr-1 inline-block">{product.quantity} × </span>
                            <div className="inline font-semibold" dangerouslySetInnerHTML={{ __html: currency_symbol }} />{printPrice(product.price) || ""}
                        </div>
                    </div>
                    <img
                        className="h-[65px] w-[65px] border border-stroke-color mb-2"
                        src={product.image.src}
                        alt={product.image.altText}
                    />
                </div>
            </div>
        )
    );
};

export default CartSidebar;
